import { useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, Grid, Heading, Text, Image, Input, InputGroup,
    InputLeftElement, IconButton, Button, Skeleton, SkeletonText,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { isEmpty, isNil, omitBy } from 'lodash';
import ProductItemNew from 'components/Dashboard/ProductItemNew';
import ApiPagination from 'components/Dashboard/ApiPagination';
import { ITEMS_PER_PAGE } from 'utils/utils';
import useDebounce from 'hooks/useDebounce';
import { get_product_by_brand } from 'redux/actions/productActions';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — match dashboard / SingleVendor
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const PER_PAGE = ITEMS_PER_PAGE ?? 12;

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SearchIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

const BackIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const TagIcon = (props) => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M21.41 11.58L12.41 2.58C12.04 2.21 11.53 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.53 2.21 12.04 2.59 12.42L11.59 21.42C11.95 21.79 12.44 22 13 22C13.56 22 14.05 21.79 14.41 21.41L21.41 14.41C21.79 14.05 22 13.56 22 13C22 12.44 21.78 11.95 21.41 11.58ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5Z" />
    </svg>
);

const toInitial = (name) => (name?.[0] ?? '?').toUpperCase();

/* ─────────────────────────────────────────────────────────────
   BRAND HERO — clean header, no banner image
   ───────────────────────────────────────────────────────────── */
const BrandHeader = ({ brand, searchTerm, onSearchChange, productCount, loading }) => {
    const initial = toInitial(brand?.name);

    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="14px"
            boxShadow={SHADOW_SM}
            p={{ base: '14px', md: '20px' }}
            mb="18px"
            minW="0"
        >
            <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: '14px', md: '20px' }}
                align={{ base: 'stretch', md: 'center' }}
                justify="space-between"
                minW="0"
            >
                {/* LEFT — logo + brand name + product count */}
                <Flex gap="14px" minW="0" flex="1" align="center">
                    <Box
                        w={{ base: '56px', md: '72px' }}
                        h={{ base: '56px', md: '72px' }}
                        borderRadius="14px"
                        overflow="hidden"
                        bg={brand?.image ? SURFACE : CYAN}
                        flexShrink={0}
                        border="1px solid"
                        borderColor={BORDER}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color={NAVY}
                    >
                        {brand?.image ? (
                            <Image
                                src={brand.image}
                                alt={brand.name}
                                w="full"
                                h="full"
                                objectFit="cover"
                                fallback={
                                    <Flex w="full" h="full" align="center" justify="center"
                                        bg={CYAN} fontSize={{ base: '22px', md: '28px' }}
                                        fontWeight="800" fontFamily={FONT} color={NAVY}>
                                        {initial}
                                    </Flex>
                                }
                            />
                        ) : (
                            <Text
                                fontSize={{ base: '22px', md: '28px' }}
                                fontWeight="800"
                                fontFamily={FONT}
                            >
                                {initial}
                            </Text>
                        )}
                    </Box>

                    <Box minW="0" flex="1">
                        <Heading
                            as="h1"
                            fontSize={{ base: '20px', md: '26px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.2"
                            noOfLines={2}
                            mb="2px"
                        >
                            {brand?.name ?? 'Brand'}
                        </Heading>
                        <Text fontSize="11px" color={MUTED} fontWeight="600">
                            {loading
                                ? 'Loading products…'
                                : productCount != null
                                ? `${productCount} product${productCount === 1 ? '' : 's'}`
                                : ''}
                        </Text>
                    </Box>
                </Flex>

                {/* RIGHT — search */}
                <Box
                    flexShrink={0}
                    w={{ base: 'full', md: 'auto' }}
                    minW={{ md: '280px' }}
                    maxW={{ md: '340px' }}
                >
                    <InputGroup>
                        <InputLeftElement pointerEvents="none" h="38px">
                            <Box color={FAINT}>
                                <SearchIcon />
                            </Box>
                        </InputLeftElement>
                        <Input
                            h="38px"
                            placeholder={`Search ${brand?.name ?? 'products'}`}
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            bg={SURFACE}
                            border="1px solid"
                            borderColor={BORDER}
                            fontSize="12px"
                            fontFamily={FONT}
                            borderRadius="8px"
                            _focus={{ borderColor: NAVY, boxShadow: `0 0 0 1px ${NAVY}` }}
                            _placeholder={{ color: FAINT, fontSize: '12px' }}
                        />
                        {searchTerm && (
                            <IconButton
                                aria-label="Clear search"
                                icon={<CloseIcon />}
                                onClick={() => onSearchChange('')}
                                size="xs"
                                position="absolute"
                                right="6px"
                                top="50%"
                                transform="translateY(-50%)"
                                bg="transparent"
                                color={FAINT}
                                _hover={{ bg: SURFACE_ALT, color: INK }}
                                zIndex={2}
                            />
                        )}
                    </InputGroup>
                </Box>
            </Flex>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   PRODUCT GRID SKELETON — matches dashboard breakpoints
   ───────────────────────────────────────────────────────────── */
const ProductGridSkeleton = ({ count = PER_PAGE }) => (
    <Grid
        templateColumns={{
            base: 'repeat(2, 1fr)',
            sm:   'repeat(2, 1fr)',
            md:   'repeat(3, 1fr)',
            lg:   'repeat(4, 1fr)',
            xl:   'repeat(5, 1fr)',
        }}
        gap={{ base: '10px', md: '14px' }}
        w="full"
        minW="0"
    >
        {Array.from({ length: count }).map((_, i) => (
            <Box
                key={i}
                bg={SURFACE}
                borderRadius="10px"
                overflow="hidden"
                border="1px solid"
                borderColor={BORDER}
            >
                <Skeleton h="160px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
                <Box p="10px">
                    <SkeletonText
                        noOfLines={2}
                        spacing="6px"
                        skeletonHeight="9px"
                        startColor={SURFACE_ALT}
                        endColor={BORDER}
                    />
                    <Skeleton
                        mt="6px"
                        h="11px"
                        w="40%"
                        startColor={SURFACE_ALT}
                        endColor={BORDER}
                    />
                </Box>
            </Box>
        ))}
    </Grid>
);

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */
const EmptyState = ({ brandName, hasSearch, onClearSearch }) => (
    <Box
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="12px"
        p="40px 20px"
        textAlign="center"
    >
        <Flex justify="center" mb="10px" color={FAINT}>
            <TagIcon />
        </Flex>
        <Text fontSize="13px" color={INK} mb="4px" fontWeight="700">
            {hasSearch
                ? `No products match "${hasSearch}"`
                : `No products from ${brandName ?? 'this brand'} yet`}
        </Text>
        <Text fontSize="11px" color={FAINT} mb={hasSearch ? '10px' : '0'}>
            {hasSearch
                ? 'Try a different search term'
                : 'Check back soon — new stock arrives regularly.'}
        </Text>
        {hasSearch && (
            <Button
                size="xs"
                variant="link"
                color={NAVY}
                fontWeight="700"
                onClick={onClearSearch}
            >
                Clear search
            </Button>
        )}
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   SINGLE BRAND — main page
   ───────────────────────────────────────────────────────────── */
const SingleBrand = () => {
    const { id: brandId } = useParams();
    // const navigate = useNavigate();
    const dispatch = useDispatch();

    const { brandItems, loading, brands } = useSelector(
        (state) => state.productReducer
    );

    // const [filters, setFilters] = useState({});
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);

    /* Resolve the brand from Redux (looked up by brand_id from URL) */
    const brand = useMemo(() => {
        if (!Array.isArray(brands)) return null;
        return brands.find(
            (b) => String(b?.brand_id) === String(brandId) || b?.id === brandId
        ) ?? null;
    }, [brands, brandId]);

    /* Fetch products for this brand — fires whenever brand name resolves
       or search/filters change */
    useEffect(() => {
        const brandName = brand?.name;
        if (!brandName) return;

        dispatch(
            get_product_by_brand(brandName, {
                per_page: PER_PAGE,
                page: page + 1,                /* Backend is 1-indexed */
                name: debouncedSearch,
                ...omitBy( isNil),
            }),
        );
    }, [dispatch, brand?.name, page, debouncedSearch,]);

    /* Reset to page 0 when search/filters change */
    useEffect(() => {
        if (debouncedSearch || !isEmpty(omitBy( isNil))) {
            setPage(0);
        }
    }, [debouncedSearch,]);

    const handleSearchChange = (val) => {
        setSearchTerm(val);
        if (page !== 0) setPage(0);
    };

    const items = brandItems?.items ?? [];
    const hasItems = items.length > 0;
    const totalCount = brandItems?.page_context?.total ?? null;
    const hasMorePage = brandItems?.page_context?.has_more_page ?? false;

    return (
        <Box
            w="full"
            maxW="100%"
            minW="0"
            overflowX="hidden"
            px={{ base: '12px', md: '20px' }}
            py="20px"
            fontFamily={FONT}
            bg={SURFACE_ALT}
            minH="100vh"
        >
            <Box maxW="1400px" mx="auto" w="full">
                {/* Breadcrumb */}
                <Flex align="center" gap="6px" mb="14px" color={MUTED} fontSize="12px">
                    <Button
                        as={RouterLink}
                        to="/dashboard/brands"
                        variant="link"
                        color={MUTED}
                        fontWeight="600"
                        fontSize="12px"
                        leftIcon={<BackIcon />}
                        _hover={{ color: NAVY, textDecoration: 'none' }}
                        minW="auto"
                        h="auto"
                    >
                        All brands
                    </Button>
                    {brand?.name && (
                        <>
                            <Text color={FAINT}>/</Text>
                            <Text color={INK} fontWeight="700" noOfLines={1}>
                                {brand.name}
                            </Text>
                        </>
                    )}
                </Flex>

                {/* Brand header (clean — no banner) */}
                <BrandHeader
                    brand={brand}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    productCount={totalCount}
                    loading={loading && !hasItems}
                />

                {/* Products section title */}
                <Flex
                    justify="space-between"
                    align="baseline"
                    mb="12px"
                    gap="10px"
                    minW="0"
                    flexWrap="wrap"
                >
                    <Heading
                        as="h2"
                        fontSize={{ base: '14px', md: '16px' }}
                        fontWeight="800"
                        color={INK}
                        fontFamily={FONT}
                        letterSpacing="-0.01em"
                    >
                        Products
                    </Heading>
                </Flex>

                {/* Products grid OR empty state OR skeleton */}
                {loading && !hasItems ? (
                    <ProductGridSkeleton />
                ) : !hasItems ? (
                    <EmptyState
                        brandName={brand?.name}
                        hasSearch={debouncedSearch}
                        onClearSearch={() => handleSearchChange('')}
                    />
                ) : (
                    <>
                        {/* Subtle indicator when background refetch is happening */}
                        {loading && hasItems && (
                            <Flex justify="center" mb="8px">
                                <Text fontSize="10px" color={FAINT} fontWeight="700" letterSpacing="0.04em">
                                    UPDATING…
                                </Text>
                            </Flex>
                        )}

                        <Grid
                            templateColumns={{
                                base: 'repeat(2, 1fr)',
                                sm:   'repeat(2, 1fr)',
                                md:   'repeat(3, 1fr)',
                                lg:   'repeat(4, 1fr)',
                                xl:   'repeat(5, 1fr)',
                            }}
                            gap={{ base: '10px', md: '14px' }}
                            w="full"
                            minW="0"
                        >
                            {items.map((item) => (
                                <Box
                                    key={item.item_id ?? item.id}
                                    minW="0"
                                    w="full"
                                    display="flex"
                                    flexDirection="column"
                                    h="full"
                                    sx={{
                                        '& > *': {
                                            flex: 1,
                                            height: '100%',
                                            marginBottom: '0 !important',
                                        },
                                    }}
                                >
                                    <ProductItemNew item={item} />
                                </Box>
                            ))}
                        </Grid>

                        {/* Pagination */}
                        {(hasMorePage || page > 0) && (
                            <Box mt="20px" display="flex" justifyContent="center">
                                <ApiPagination
                                    page={page}
                                    setPage={setPage}
                                    hasMorePages={hasMorePage}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default SingleBrand;